import { Alert, Stack } from "@mui/material";
import AppDialog from "components/common/AppDialog";
import CustomButton from "components/common/CustomButton";
import { useCreateWebhookTriggerMutation } from "features/integrationsBeta/webhookTriggers";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useState } from "react";
import { formatApiError } from "utils/formatApiError";
import WebhookTriggerFields from "./WebhookTriggerFields";

interface Props {
  open: boolean;
  onClose: () => void;
  integrationId: number;
  clientId: number;
}

/**
 * Create an inbound webhook trigger via the shared trigger fields. The
 * backend additionally validates that the integration is webhook-triggered
 * (derived from its steps).
 */
export default function WebhookTriggerCreateDialog({
  open,
  onClose,
  integrationId,
  clientId,
}: Props) {
  const dispatch = useAppDispatch();
  const [createTrigger, { isLoading }] = useCreateWebhookTriggerMutation();

  const [name, setName] = useState("");
  const [provider, setProvider] = useState("");
  const [connection, setConnection] = useState<number | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!provider || connection == null) return;
    try {
      const created = await createTrigger({
        integration: integrationId,
        provider,
        connection,
        ...(name.trim() ? { name: name.trim() } : {}),
      }).unwrap();
      dispatch(
        pushNotification({
          severity: "success",
          message: `Webhook trigger created. Paste ${created.webhook_url} into the provider UI and verify it.`,
        }),
      );
      setName("");
      setConnection(null);
      setApiError(null);
      onClose();
    } catch (err) {
      setApiError(
        formatApiError(
          (err as { data?: unknown })?.data,
          "Could not create the webhook trigger.",
        ),
      );
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Add webhook trigger"
      maxWidth="sm"
      showCloseIcon
      actions={
        <>
          <CustomButton fullWidth={false} onClick={onClose}>
            Cancel
          </CustomButton>
          <CustomButton
            fullWidth={false}
            onClick={handleSave}
            disabled={isLoading || !provider || connection == null}
          >
            Create
          </CustomButton>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <WebhookTriggerFields
          clientId={clientId}
          name={name}
          onNameChange={setName}
          provider={provider}
          onProviderChange={setProvider}
          connection={connection}
          onConnectionChange={setConnection}
        />
        {apiError && <Alert severity="error">{apiError}</Alert>}
      </Stack>
    </AppDialog>
  );
}
